// ---------------------------------------------------------------------------
// Form Rendering Utilities
// ---------------------------------------------------------------------------

import { attr, text, url as urlEscape } from "./escape.js";
import type { RenderContext } from "./types.js";

export function buildFormAction(ctx: RenderContext): string {
  return ctx.submissionEndpoint;
}

const TURNSTILE_API_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js";

/**
 * Renders the Cloudflare Turnstile widget + loader script when the render
 * context carries a `turnstileSiteKey`. Returns an empty string otherwise,
 * preserving prior behavior for tests and tenants without Turnstile wired.
 *
 * SEC-K K-3 / SEC-S: every Puck-emitted lead/contact form MUST surface this
 * widget so the server-side `verifyTurnstile()` middleware (FS-D, SEC-L) has
 * a token to validate on submit. Site key is public by spec — safe to inline.
 */
export function buildTurnstileWidget(ctx: RenderContext): string {
  const siteKey = ctx.turnstileSiteKey;
  if (typeof siteKey !== "string" || siteKey.trim().length === 0) {
    return "";
  }
  return [
    `<div class="ease-field ease-field-turnstile">`,
    `  <div class="cf-turnstile" data-sitekey="${attr(siteKey.trim())}" data-callback="onTurnstileSuccess"></div>`,
    `</div>`,
    `<script async defer src="${attr(TURNSTILE_API_URL)}"></script>`,
  ].join("\n  ");
}

export type CrmFollowUpPolicy = "follow_up" | "contact_only" | "create_deal";

export interface FormHiddenFieldOptions {
  crmFollowUpPolicy?: CrmFollowUpPolicy;
}

export function buildHiddenFields(ctx: RenderContext, opts: FormHiddenFieldOptions = {}): string {
  return [
    `<input type="hidden" name="_pageId" value="${attr(ctx.pageId)}">`,
    `<input type="hidden" name="_tenantId" value="${attr(ctx.tenantId)}">`,
    `<input type="hidden" name="_versionId" value="${attr(ctx.versionId)}">`,
    ...(opts.crmFollowUpPolicy ? [`<input type="hidden" name="_crm_follow_up_policy" value="${attr(opts.crmFollowUpPolicy)}">`] : []),
    // Honeypot — bots fill this, humans don't (hidden via styles)
    `<input type="hidden" name="_hp" value="" aria-hidden="true" tabindex="-1" autocomplete="off">`,
    // CSRF token placeholder — injected at serve time by page-serve (Security 6.5)
    `<input type="hidden" name="_tok" value="">`,
  ].join("\n");
}

export interface FieldCondition {
  /** Field name this condition depends on */
  field: string;
  /** Operator for comparison */
  op: "eq" | "neq" | "contains" | "not_empty";
  /** Value to compare against (unused for not_empty) */
  value?: string;
}

export interface FormField {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  accept?: string;
  multiple?: boolean;
  /** Conditions that control field visibility — all must be true to show */
  conditions?: FieldCondition[];
}

function buildInputAutocomplete(field: FormField): string | null {
  const normalizedName = field.name.trim().toLowerCase();

  if (field.type === "email" || normalizedName.includes("email")) return "email";
  if (field.type === "tel" || normalizedName.includes("phone")) return "tel";
  if (normalizedName === "name" || normalizedName.includes("full_name")) return "name";
  if (normalizedName.includes("first")) return "given-name";
  if (normalizedName.includes("last")) return "family-name";
  if (normalizedName.includes("company")) return "organization";
  if (normalizedName.includes("city")) return "address-level2";
  if (normalizedName.includes("url") || field.type === "url") return "url";

  return null;
}

function buildInputMode(field: FormField): string | null {
  if (field.type === "email") return "email";
  if (field.type === "tel") return "tel";
  if (field.type === "number") return "numeric";
  if (field.type === "url") return "url";

  return null;
}

function buildRequiredHint(field: FormField, isConditional: boolean): string {
  if (!field.required) return "";

  const requirement = isConditional
    ? "Required when this field is shown"
    : "Required field";

  return `<span id="${attr(`${field.name}-hint`)}" class="ease-sr-only">${text(requirement)}</span>`;
}

function buildFieldControlAttrs(field: FormField, isConditional: boolean): string {
  const attrs: string[] = [];

  if (field.required && !isConditional) {
    attrs.push('required aria-required="true"');
  }

  if (field.required) {
    attrs.push(`aria-describedby="${attr(`${field.name}-hint`)}"`);
  }

  const autocomplete = buildInputAutocomplete(field);
  if (autocomplete) {
    attrs.push(`autocomplete="${attr(autocomplete)}"`);
  }

  const inputMode = buildInputMode(field);
  if (inputMode && field.type !== "select" && field.type !== "textarea") {
    attrs.push(`inputmode="${attr(inputMode)}"`);
  }

  if (field.required && isConditional) {
    attrs.push('data-cond-required="true"');
  }

  if (field.type === "file") {
    if (field.accept) {
      attrs.push(`accept="${attr(field.accept)}"`);
    }
    if (field.multiple) {
      attrs.push("multiple");
    }
  }

  return attrs.length > 0 ? ` ${attrs.join(" ")}` : "";
}

function buildConditionAttrs(field: FormField): string {
  if (!field.conditions?.length) return "";
  const encoded = JSON.stringify(field.conditions);
  return ` data-condition="${attr(encoded)}" hidden aria-hidden="true"`;
}

export function renderField(field: FormField): string {
  const id = `field-${attr(field.name)}`;
  const isConditional = (field.conditions?.length ?? 0) > 0;
  const controlAttrs = buildFieldControlAttrs(field, isConditional);
  const condAttrs = buildConditionAttrs(field);
  const requiredHint = buildRequiredHint(field, isConditional);
  const label = `<label for="${id}">${text(field.label)}${field.required ? ' <span aria-hidden="true">*</span><span class="ease-sr-only"> (required)</span>' : ""}</label>`;

  switch (field.type) {
    case "file":
      return `<div class="ease-field"${condAttrs}>
  ${label}
  ${requiredHint}
  <input type="file" id="${id}" name="${attr(field.name)}"${controlAttrs}>
</div>`;

    case "textarea":
      return `<div class="ease-field"${condAttrs}>
  ${label}
  ${requiredHint}
  <textarea id="${id}" name="${attr(field.name)}" placeholder="${attr(field.placeholder ?? "")}"${controlAttrs}></textarea>
</div>`;

    case "select":
      return `<div class="ease-field"${condAttrs}>
  ${label}
  ${requiredHint}
  <select id="${id}" name="${attr(field.name)}"${controlAttrs}>
    <option value="">${text(field.placeholder ?? "Select...")}</option>
    ${(field.options ?? []).map((o) => `<option value="${attr(o.value)}">${text(o.label)}</option>`).join("\n    ")}
  </select>
</div>`;

    default:
      return `<div class="ease-field"${condAttrs}>
  ${label}
  ${requiredHint}
  <input type="${attr(field.type)}" id="${id}" name="${attr(field.name)}" placeholder="${attr(field.placeholder ?? "")}"${controlAttrs}>
</div>`;
  }
}

export function formRequiresMultipart(fields: FormField[]): boolean {
  return fields.some((field) => field.type === "file");
}

/**
 * Generate inline JS that handles conditional field visibility.
 * No external dependencies. Uses event delegation on the form.
 * Hidden fields are excluded from submission via disabled attribute.
 */
export function buildConditionalLogicScript(): string {
  return `<script>
(function(){
  var forms=document.querySelectorAll('[data-ease-form]');
  forms.forEach(function(form){
    var condFields=form.querySelectorAll('[data-condition]');
    if(!condFields.length)return;
    function evalCond(c,form){
      var el=form.querySelector('[name="'+c.field+'"]');
      if(!el)return false;
      var v=el.value||'';
      switch(c.op){
        case 'eq':return v===c.value;
        case 'neq':return v!==c.value;
        case 'contains':return v.indexOf(c.value||'')!==-1;
        case 'not_empty':return v.length>0;
        default:return false;
      }
    }
    function update(){
      condFields.forEach(function(div){
        var conds=JSON.parse(div.getAttribute('data-condition')||'[]');
        var show=conds.every(function(c){return evalCond(c,form);});
        div.hidden=!show;
        div.setAttribute('aria-hidden',String(!show));
        var inputs=div.querySelectorAll('input,select,textarea');
        inputs.forEach(function(inp){
          if(show){
            inp.removeAttribute('disabled');
            if(inp.getAttribute('data-cond-required')==='true'){inp.setAttribute('required','');inp.setAttribute('aria-required','true');}
          }else{
            inp.setAttribute('disabled','');
            inp.removeAttribute('required');
            inp.removeAttribute('aria-required');
          }
        });
      });
    }
    form.addEventListener('input',update);
    form.addEventListener('change',update);
    update();
  });
})();
</script>`;
}
